import {
  FontFace as CanvasFontFace,
  type FontFaceRule,
  getFontFaceRulesFromUrl,
  setTextAreaElementOptions,
  setTextElementOptions,
} from '@evolutivelabs/amuse-design-canvas'

import { fallbackFonts, fonts, GOOGLE_FONTS_NAME_MAP } from '@/materials/fonts.config'

/**
 * U+0041 ('A')：用於識別 Preview Rule 的代表性 ASCII code point。
 * Google Fonts 將字型拆分為 ~100 個 unicode-range 子集；Preview Rule 是其中涵蓋
 * ASCII 的子集，用於在字型選單中以正確字體渲染字型名稱（例如「Anton」）。
 *
 * `codePointAt()` 回傳 `number | undefined`（TypeScript 無法靜態推論字串非空），
 * `?? 0x41` 作為型別安全 fallback，執行期永遠不會觸發。
 */
const PREVIEW_CODE_POINT = 'A'.codePointAt(0) ?? 0x41

/**
 * 為 CanvasFontFace 加上防崩潰包裝。
 *
 * 問題根源：
 * - Vite 生產構建的最小化會重命名變數（如 `e.tables`）並重排物件屬性
 * - Safari JavaScriptCore 對最小化後的 fontkit 解析程式碼比 Chrome V8 更嚴格
 * - 兩者疊加後，在解析 Noto Sans TC WOFF2 unicode-range 子集時拋出
 *   `TypeError: undefined is not an object (evaluating 'e.tables')`
 * - 開發模式（未最小化）與 Chrome（V8）皆不受影響
 *
 * 解法：
 * - 將四個方法（getFont / getMetrics / hasGlyphForCodePoint / layout）
 *   以 try/catch 包裝，解析失敗時回傳安全預設值
 * - 確保 canvas 的回退字體鏈（fallbackFonts）能接手渲染，不至於崩潰
 */
function createSafeCanvasFontFace(fontFamily: string, rule: FontFaceRule): CanvasFontFace {
  const face = new CanvasFontFace(rule)
  // 任一方法首次拋錯後標記為損壞，後續呼叫直接回傳預設值，避免重複記錄 log
  let isBroken = false

  function markAsBroken(operation: string, error: unknown): void {
    if (isBroken) return
    isBroken = true
    const userAgent = navigator.userAgent.toLowerCase()
    const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome')
    const environment = import.meta.env.PROD ? 'prod' : 'dev'
    console.error(`[FontDebug] Canvas font parse failed URL: ${rule.src}`)
    console.error('[FontDebug] Canvas font parse failed:', {
      fontFamily,
      src: rule.src,
      operation,
      isSafari,
      environment,
      error,
    })
  }

  const getFont = face.getFont.bind(face)
  face.getFont = async (): ReturnType<CanvasFontFace['getFont']> => {
    if (isBroken) return null
    try {
      return await getFont()
    } catch (error: unknown) {
      markAsBroken('getFont', error)
      return null
    }
  }

  const getMetrics = face.getMetrics.bind(face)
  face.getMetrics = async () => {
    if (isBroken) {
      return { ascent: 0, descent: 0, lineGap: 0, unitsPerEm: 0 }
    }
    try {
      return await getMetrics()
    } catch (error: unknown) {
      markAsBroken('getMetrics', error)
      return { ascent: 0, descent: 0, lineGap: 0, unitsPerEm: 0 }
    }
  }

  const hasGlyphForCodePoint = face.hasGlyphForCodePoint.bind(face)
  face.hasGlyphForCodePoint = async (codePoint: number) => {
    if (isBroken) return false
    try {
      return await hasGlyphForCodePoint(codePoint)
    } catch (error: unknown) {
      markAsBroken('hasGlyphForCodePoint', error)
      return false
    }
  }

  const layout = face.layout.bind(face)
  face.layout = async (text: string, glyphs) => {
    if (isBroken) return 0
    try {
      return await layout(text, glyphs)
    } catch (error: unknown) {
      markAsBroken('layout', error)
      return 0
    }
  }

  return face
}

async function fetchGoogleFontRules(): Promise<Map<string, FontFaceRule[]>> {
  const fontSourceMap = new Map<string, FontFaceRule[]>()
  // 只讀 data-canvas-fonts link，避免混入 UI 介面用的多 weight CSS（兩個 link 消費者不同）
  const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[data-canvas-fonts]'))

  await Promise.all(
    links.map(async (link): Promise<void> => {
      try {
        await getFontFaceRulesFromUrl(link.href, fontSourceMap)
      } catch (error: unknown) {
        console.error('[FontDebug] Failed to fetch font rules from', link.href, error)
      }
    }),
  )
  return fontSourceMap
}

/**
 * module 載入時立即發出 Google Fonts CSS 請求（non-blocking）。
 * 讓 getFontFaceMap() 首次被 canvas 呼叫時，fetch 已完成或進行中，
 * 避免文字元素渲染時才開始等待網路而延遲 FCP。
 */
const _fontSourceMapPromise = fetchGoogleFontRules()

/**
 * 將 Google Fonts 的所有 unicode-range 子集以 enum 名稱注入 document.fonts。
 *
 * - Preview Rule（涵蓋 ASCII）：立即 `load()`，確保字型選單能立即以正確字體顯示名稱。
 * - 其他子集：僅 `add()` 至 document.fonts，瀏覽器在 textarea 遇到對應文字時按需下載。
 */
function injectBrowserFontFaces(
  enumFont: string,
  rules: FontFaceRule[],
  previewRule: FontFaceRule,
): void {
  for (const rule of rules) {
    const descriptor: FontFaceDescriptors | undefined =
      rule.unicodeRange === undefined ? undefined : { unicodeRange: rule.unicodeRange.toString() }
    const browserFace = new window.FontFace(enumFont, `url('${rule.src}')`, descriptor)
    if (rule === previewRule) {
      // Preview Rule（涵蓋 ASCII）：eager load，確保字型選單立即以正確字體顯示名稱
      browserFace
        .load()
        .then(() => document.fonts.add(browserFace))
        .catch((error: unknown) => {
          console.error('[FontDebug] Browser FontFace load failed:', enumFont, error)
        })
    } else {
      // 其他子集：僅加入 document.fonts，瀏覽器遇到對應文字時按需下載
      document.fonts.add(browserFace)
    }
  }
}

/**
 * 建立供 canvas 使用的 FontFace map。
 *
 * - Google Fonts 字體：直接 fetch CSS URL（而非讀 document.styleSheets 以避免 preload 時序問題），
 *   取得含 unicode-range 規則後建立 CanvasFontFace；fontkit 按需載入對應 WOFF2 子集。
 *   同時以 enum 名稱注入 document.fonts，供字型選單 CSS 預覽使用。
 * - CDN 字體（非 Noto Sans）：以完整字體 URL 建立 CanvasFontFace，無 unicode-range 分割。
 */
let _cachedFontFaceMap: Map<string, CanvasFontFace[]> | undefined

export async function getFontFaceMap(): Promise<Map<string, CanvasFontFace[]>> {
  if (_cachedFontFaceMap !== undefined) return _cachedFontFaceMap

  const fontSourceMap = await _fontSourceMapPromise
  const fontFaceMap = new Map<string, CanvasFontFace[]>()

  for (const [googleName, enumFonts] of Object.entries(GOOGLE_FONTS_NAME_MAP)) {
    const rules = fontSourceMap.get(googleName) ?? []
    if (rules.length === 0) continue

    // canvas 用：每個 unicode-range 子集對應一個 CanvasFontFace，fontkit 按需載入對應 WOFF2
    const canvasFaces = rules.map((rule) => createSafeCanvasFontFace(googleName, rule))
    for (const enumFont of enumFonts) {
      fontFaceMap.set(enumFont, canvasFaces)
    }

    // Preview Rule：無 unicodeRange 限制（涵蓋全字符）或明確包含 U+0041（ASCII 'A'）
    const previewRule = rules.find(
      (rule) => rule.unicodeRange === undefined || rule.unicodeRange.has(PREVIEW_CODE_POINT),
    )

    if (previewRule === undefined) continue

    // 瀏覽器用：以 enum 名稱注入所有子集，確保 textarea 能以正確字體渲染各語系文字
    for (const enumFont of enumFonts) {
      injectBrowserFontFaces(enumFont, rules, previewRule)
    }
  }

  // CDN 字體（非 Noto Sans）：canvas 用，完整字體 URL，無 unicode-range 分割
  for (const [font, url] of Object.entries(fonts)) {
    if (!fontFaceMap.has(font)) {
      fontFaceMap.set(font, [createSafeCanvasFontFace(font, { src: url })])
    }
  }

  _cachedFontFaceMap = fontFaceMap
  return _cachedFontFaceMap
}

export function initFontSettings(): void {
  setTextElementOptions({ getFontFaceMap, fallbackFonts, minScaleRatio: 1.5 })
  setTextAreaElementOptions({ getFontFaceMap, fallbackFonts })
}
